/**
 * Google "Almost there" consent step (T49.3).
 *
 * Surfaced when `POST /api/auth/google` returns `{ consentRequired: true }` for a
 * NEW Google user who reached the endpoint without a consent signal. Rather than
 * dead-ending or dumping the user back to a blank form, this is a calm,
 * single-purpose moment in the SAME card: a celebratory "Almost there" heading,
 * the same Terms + Privacy consent checkbox, one primary "Finish signing up"
 * button, and a quiet "Use a different account" escape so it's never a trap
 * (Designer spec §3B).
 *
 * On tick + Finish, the caller re-runs the Google credential exchange with
 * `termsAccepted: true` (the parent owns the credential + the mutation); this
 * component owns only the consent UI + the calm unticked nudge. Tokens-only (CP5);
 * the heading uses the Poppins display face like the other auth `h1`s.
 */

import { useCallback, useRef, useState } from 'react';
import { Button, Card, Checkbox } from '../../components';
import {
  authCardBodyStyle,
  authHeadingStyle,
  authSubheadingStyle,
  authConsentRowStyle,
  authConsentHintStyle,
  authConsentLinkStyle,
  consentLinkButtonReset,
  authInlineNoteStyle,
} from './authLayout';
import { LegalConsentOverlay } from './LegalConsentOverlay';
import type { LegalSlug } from '../legal/content';

const CONSENT_HINT = 'Just tick the box above and you’re in.';
const CONSENT_HINT_ID = 'google-consent-hint';

export interface GoogleConsentStepProps {
  /** Optional name/email echo for the celebratory heading ("Almost there, …"). */
  who?: string | null;
  /** True while the re-run Google call is in flight (disables Finish). */
  pending?: boolean;
  /** Tick + Finish → re-run the Google exchange with consent. */
  onFinish: () => void;
  /** Quiet escape back to the normal sign-in screen. */
  onUseDifferentAccount: () => void;
}

export function GoogleConsentStep({
  who,
  pending,
  onFinish,
  onUseDifferentAccount,
}: GoogleConsentStepProps): JSX.Element {
  const [consent, setConsent] = useState(false);
  const [nudge, setNudge] = useState(false);
  const consentRef = useRef<HTMLInputElement | null>(null);
  // BUG42 — which legal doc the in-app overlay is showing (null = closed). Same
  // pattern as CreateAccountPage: the overlay renders OVER this step so opening it
  // never unmounts the step (the parent's stashed Google credential survives) and
  // never navigates away — the Android WebView never dropped this step again.
  const [legalOverlaySlug, setLegalOverlaySlug] = useState<LegalSlug | null>(null);
  const termsButtonRef = useRef<HTMLButtonElement | null>(null);
  const privacyButtonRef = useRef<HTMLButtonElement | null>(null);
  const activeTriggerRef = legalOverlaySlug === 'privacy' ? privacyButtonRef : termsButtonRef;

  const handleFinish = useCallback(() => {
    if (!consent) {
      // Same calm nudge as the Create path — never a red error here.
      setNudge(true);
      consentRef.current?.focus();
      return;
    }
    onFinish();
  }, [consent, onFinish]);

  const heading = who ? `Almost there, ${who}` : 'Almost there';

  return (
    <>
    <Card padding="lg" shadow="md" data-testid="google-consent-step">
      <div style={authCardBodyStyle}>
        <h1 style={authHeadingStyle}>{heading}</h1>
        <p style={authSubheadingStyle}>One last thing before you’re in.</p>

        <div style={authConsentRowStyle} data-testid="google-consent-row">
          <Checkbox
            ref={consentRef}
            checked={consent}
            nudge={nudge}
            describedById={nudge ? CONSENT_HINT_ID : undefined}
            onChange={(e) => {
              setConsent(e.target.checked);
              if (e.target.checked) setNudge(false);
            }}
            data-testid="google-consent-checkbox"
            label={
              // BUG42 — open the legal docs in the in-app overlay, NOT a router
              // nav. As <Link>s these SPA-navigated away and unmounted this step
              // (dropping the parent's stashed Google credential → restart signup).
              // As buttons that open LegalConsentOverlay, the step stays mounted on
              // web AND in the native WebView. stopPropagation keeps the tap off the
              // checkbox toggle; the buttons are styled to look like the old links.
              <>
                I agree to the{' '}
                <button
                  ref={termsButtonRef}
                  type="button"
                  style={{ ...authConsentLinkStyle, ...consentLinkButtonReset }}
                  data-testid="google-consent-terms"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLegalOverlaySlug('terms');
                  }}
                >
                  Terms of Service
                </button>{' '}
                and{' '}
                <button
                  ref={privacyButtonRef}
                  type="button"
                  style={{ ...authConsentLinkStyle, ...consentLinkButtonReset }}
                  data-testid="google-consent-privacy"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLegalOverlaySlug('privacy');
                  }}
                >
                  Privacy Policy
                </button>
                .
              </>
            }
          />
          {nudge && (
            <p
              id={CONSENT_HINT_ID}
              role="status"
              style={authConsentHintStyle}
              data-testid="google-consent-hint"
            >
              {CONSENT_HINT}
            </p>
          )}
        </div>

        <Button
          type="button"
          variant="primary"
          disabled={pending}
          onClick={handleFinish}
          data-testid="google-consent-submit"
        >
          {pending ? 'Finishing…' : 'Finish signing up'}
        </Button>

        <p style={authInlineNoteStyle}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onUseDifferentAccount}
            data-testid="google-consent-switch-account"
          >
            Use a different account
          </Button>
        </p>
      </div>
    </Card>

    {/* BUG42 — the in-app legal overlay. Portals over this step (via the shared
        Modal) so opening it never unmounts the step; closing returns focus to the
        consent button that opened it. */}
    <LegalConsentOverlay
      slug={legalOverlaySlug}
      onClose={() => setLegalOverlaySlug(null)}
      triggerRef={activeTriggerRef}
    />
    </>
  );
}
